import React from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import NoticeCardPreview from "./NoticeCardPreview";
import { composerSectionHeadingSx } from "./ComposerStepCard";
import {
  dateBlankValue,
  dateFromBlankValue,
  fillTemplateNotice,
  type TemplateNoticeText,
  type TemplateBlank,
  type TemplateBlankValues,
} from "../../domain/notices/templateBlanks";
import type { FillInTheBlanksStart } from "../../domain/notices/wizardSession";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../utils/eventDateDisplay";

/** The wizard's footer Continue button submits this step's form. */
export const FILL_IN_THE_BLANKS_FORM_ID = "fill-in-the-blanks";

interface FillInTheBlanksStepProps {
  start: FillInTheBlanksStart;
  values: TemplateBlankValues;
  onValuesChange: (values: TemplateBlankValues) => void;
  onContinue: (filled: TemplateNoticeText) => void;
  isEvent: boolean;
  backgroundImage?: string;
  artworkUrl?: string;
}

const BlankInput: React.FC<{
  blank: TemplateBlank;
  value: string;
  onChange: (value: string) => void;
}> = ({ blank, value, onChange }) => {
  switch (blank.type) {
    case "date":
      return (
        <DatePicker
          label={blank.label}
          format={EVENT_DATE_PICKER_FORMAT}
          value={dateFromBlankValue(value)}
          enableAccessibleFieldDOMStructure={false}
          onChange={(date: Date | null) => onChange(dateBlankValue(date))}
          slotProps={{
            textField: { fullWidth: true, size: "small" },
            popper: {
              sx: { zIndex: (theme) => theme.zIndex.modal + 4 },
            },
          }}
        />
      );
    case "time":
      return (
        <TextField
          fullWidth
          size="small"
          type="time"
          label={blank.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      );
    case "amount":
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={blank.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ min: 0 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">R</InputAdornment>,
          }}
        />
      );
    case "text":
      return (
        <TextField
          fullWidth
          size="small"
          label={blank.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

/**
 * The first step when a Template has Blanks: one input per Blank beside a live
 * preview. Continue hands back the Template's title and text, filled in.
 */
const FillInTheBlanksStep: React.FC<FillInTheBlanksStepProps> = ({
  start,
  values,
  onValuesChange,
  onContinue,
  isEvent,
  backgroundImage,
  artworkUrl,
}) => {
  const filled = fillTemplateNotice(start, values);

  return (
    <Box
      component="form"
      id={FILL_IN_THE_BLANKS_FORM_ID}
      noValidate
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onContinue(filled);
      }}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 300px" },
        gap: { xs: 2, md: 4 },
        alignItems: "start",
      }}
    >
      <Box>
        <Typography sx={{ ...composerSectionHeadingSx, mb: 0.5 }}>
          Fill in the values
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
          Fill in what changes this time. Anything you leave empty stays in
          the notice to fill in before sending.
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={EVENT_DATE_PICKER_LOCALE}
        >
          <Box sx={{ display: "grid", gap: 2 }}>
            {start.blanks.map((blank) => (
              <BlankInput
                key={blank.key}
                blank={blank}
                value={values[blank.key] ?? ""}
                onChange={(value) =>
                  onValuesChange({ ...values, [blank.key]: value })
                }
              />
            ))}
          </Box>
        </LocalizationProvider>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 300,
          mx: "auto",
          transform: { xs: "scale(0.8)", md: "scale(0.9)" },
          transformOrigin: "top center",
        }}
      >
        {artworkUrl ? <Box component="img" src={artworkUrl} alt="Template artwork" sx={{ display: "block", width: "100%", borderRadius: 1 }} /> : <NoticeCardPreview
          title={filled.title}
          body={filled.body}
          eventBackgroundImage={backgroundImage}
          isEvent={isEvent}
          layout="native"
        />}
      </Box>
    </Box>
  );
};

export default FillInTheBlanksStep;
